import {type ReactNode, useMemo, useId} from 'react'
import {Box, Text, themeGet} from '@primer/react'
import {eachDayOfInterval, endOfWeek, format, getDaysInMonth, lastDayOfMonth, startOfWeek} from 'date-fns'
import {chunk} from 'lodash-es'
import styled from 'styled-components'
import {testIdProps} from '@github-ui/test-id-props'
import type {DayNumber, DayOfWeek} from '../types'
import {BlankDay, Day} from './Day'
import {useDatePickerContext} from './Provider'

const weekdayEnum: Record<DayOfWeek, DayNumber> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
}

export interface MonthProps {
  date: Date
  'aria-describedby': string
}

const MonthComponent = styled(Box)`
  display: flex;
  flex-direction: column;
  user-select: none;
`

const MonthRow = styled(Box)`
  display: flex;
  justify-content: space-between;
`

const MonthTitle = styled(Text)`
  color: ${themeGet('colors.fg.default')};
  font-size: ${themeGet('fontSizes.1')};
  font-weight: ${themeGet('fontWeights.bold')};
  grid-area: month;
  height: ${themeGet('space.4')};
  text-align: center;
`

const WeekdayHeader = styled(Text)`
  color: ${themeGet('colors.fg.subtle')};
  font-family: ${themeGet('fonts.mono')};
  font-size: ${themeGet('fontSizes.0')};
  justify-self: center;
  min-width: 38px;
  min-height: 38px;
  text-align: center;
  padding: ${themeGet('space.3')} ${themeGet('space.1')} ${themeGet('space.2')};
`

export const Month = ({date, 'aria-describedby': descriptionId}: MonthProps) => {
  const {
    configuration: {weekStartsOn, compressedHeader, variant},
  } = useDatePickerContext()
  const title = useMemo(() => format(date, 'MMMM yyyy'), [date])

  const weekdayHeaders = useMemo(() => {
    const now = new Date(date)
    const weekOptions: {weekStartsOn: DayNumber} = {
      weekStartsOn: weekdayEnum[weekStartsOn],
    }

    return eachDayOfInterval({start: startOfWeek(now, weekOptions), end: endOfWeek(now, weekOptions)}).map(d => (
      <WeekdayHeader
        {...testIdProps('weekday-header')}
        key={`weekday-${d}-header`}
        role="columnheader"
        aria-label={format(d, 'EEEE')}
      >
        {format(d, 'EEEEEE')}
      </WeekdayHeader>
    ))
  }, [weekStartsOn, date])

  const dayComponents = useMemo(() => {
    const components: ReactNode[] = []
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1)

    // Adding blank spots for previous month
    const preBlanks = (firstDay.getDay() + (7 - weekdayEnum[weekStartsOn])) % 7
    for (let i = 0; i < preBlanks; i++) {
      components.push(<BlankDay key={`month-pre-blank-${i}`} />)
    }

    for (let i = 1; i <= getDaysInMonth(firstDay); i++) {
      const day = new Date(date.getFullYear(), date.getMonth(), i)
      components.push(<Day key={`day-component-${day.toString()}`} date={day} />)
    }

    const lastDay = lastDayOfMonth(firstDay)
    // Adding blank spots for next month
    const postBlanks = (lastDay.getDay() + (7 - weekdayEnum[weekStartsOn])) % 7
    for (let i = 6; i > postBlanks; i--) {
      components.push(<BlankDay key={`month-post-blank-${i}`} />)
    }
    const rows = chunk(components, 7).map((days, index) => (
      <MonthRow key={`month-week-${index}`} role="row" aria-label={`Week ${index + 1}`}>
        {days}
      </MonthRow>
    ))
    return rows
  }, [weekStartsOn, date])

  const labelId = useId()

  // Hide the label with clip-path so it remains in the document for screen readers
  // Column headers are hidden because days are labeled with the week day name

  return (
    <MonthComponent
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      role="grid"
      aria-multiselectable={variant === 'multi'}
      {...testIdProps('month')}
    >
      <MonthTitle aria-live="polite" id={labelId} sx={{clipPath: compressedHeader ? 'Circle(0)' : undefined}}>
        {title}
      </MonthTitle>
      <MonthRow role="row" aria-hidden>
        {weekdayHeaders}
      </MonthRow>
      {dayComponents}
    </MonthComponent>
  )
}

try{ MonthComponent.displayName ||= 'MonthComponent' } catch {}
try{ MonthRow.displayName ||= 'MonthRow' } catch {}
try{ MonthTitle.displayName ||= 'MonthTitle' } catch {}
try{ WeekdayHeader.displayName ||= 'WeekdayHeader' } catch {}
try{ Month.displayName ||= 'Month' } catch {}